import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AsyncSection } from '@/app/components/AsyncSection';
import { SubmitError } from '@/app/components/SubmitError';
import { Badge } from '@/app/components/ui/Badge';
import { Button } from '@/app/components/ui/Button';
import { Card, CardHeader } from '@/app/components/ui/Card';
import { EmptyState } from '@/app/components/ui/EmptyState';
import { PageHeader } from '@/app/components/ui/PageHeader';
import { ProgressBar } from '@/app/components/ui/ProgressBar';
import { StatTile } from '@/app/components/ui/StatTile';
import type { Challenge, ChallengeList, ChallengeStatus } from '@/features/challenges/api';
import {
  createChallenge,
  getChallengeLeaderboard,
  listChallenges,
  setChallengeStatus,
} from '@/features/challenges/api';
import { NewChallengeModal } from '@/features/challenges/NewChallengeModal';
import { useActiveCompany } from '@/features/company/CompanyContext';
import { useMe } from '@/features/me/MeContext';
import { formatNumber } from '@/lib/format';
import { useAsync } from '@/lib/useAsync';

/**
 * `/challenges` — the Programs pill's Challenges page (PLAN-FASE2.md WP-05):
 * header counts, an "Actief & gepubliceerd" section for challenges the
 * company's members currently see, a "Concepten & afgerond" section for
 * everything else, and a "New challenge" flow (`NewChallengeModal`). Always
 * scoped to the active company (§ Bedrijfscontext) — the API's
 * `challenges.*` permissions are company-scoped only (see `permissions.ts`'s
 * `canSeeChallengesSection` doc), so unlike Departments/Users there is no
 * personal or unaffiliated variant here: a coach or a caller with no active
 * company sees the "select a company" state instead.
 */
export function ChallengesPage() {
  const { t } = useTranslation();
  const { company } = useActiveCompany();
  const { permissions } = useMe();

  if (company === null) {
    return (
      <section className="page flex flex-col gap-6">
        <PageHeader title={t('challenges.page.title')} lead={t('challenges.page.lead')} />
        <EmptyState text={t('challenges.page.noCompany')} />
      </section>
    );
  }

  if (!permissions.canSeeChallengesSection) {
    return (
      <section className="page flex flex-col gap-6">
        <PageHeader title={t('challenges.page.title')} />
        <EmptyState text={t('challenges.page.forbidden')} />
      </section>
    );
  }

  return <CompanyChallenges companyId={company.id} />;
}

const PUBLISHED_STATUSES: ChallengeStatus[] = ['active', 'paused'];

function CompanyChallenges({ companyId }: { companyId: string }) {
  const { t } = useTranslation();
  const [newOpen, setNewOpen] = useState(false);
  const [statusError, setStatusError] = useState<Error | null>(null);
  const state = useAsync(() => listChallenges(companyId), [companyId]);

  return (
    <section className="page flex flex-col gap-6">
      <PageHeader
        title={t('challenges.page.title')}
        lead={t('challenges.page.lead')}
        actions={
          <Button onClick={() => setNewOpen(true)}>{t('challenges.actions.newChallenge')}</Button>
        }
      />
      <SubmitError error={statusError} />
      <AsyncSection state={state} label={t('challenges.page.title')} skeleton="card">
        {(data: ChallengeList) => {
          const published = data.challenges.filter((c) => PUBLISHED_STATUSES.includes(c.status));
          const others = data.challenges.filter((c) => !PUBLISHED_STATUSES.includes(c.status));

          return (
            <div className="flex flex-col gap-6">
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                <StatTile label={t('challenges.counts.active')} value={data.counts.active} />
                <StatTile
                  label={t('challenges.counts.totalParticipants')}
                  value={data.counts.total_participants}
                />
                <StatTile label={t('challenges.counts.draft')} value={data.counts.draft} />
                <StatTile label={t('challenges.counts.completed')} value={data.counts.completed} />
              </div>

              {data.challenges.length === 0 ? (
                <EmptyState text={t('challenges.page.empty')} />
              ) : (
                <>
                  <ChallengeSection
                    title={t('challenges.sections.activePublished')}
                    challenges={published}
                    onStatusError={setStatusError}
                    onChanged={state.reload}
                  />
                  <ChallengeSection
                    title={t('challenges.sections.draftsCompleted')}
                    challenges={others}
                    onStatusError={setStatusError}
                    onChanged={state.reload}
                  />
                </>
              )}
            </div>
          );
        }}
      </AsyncSection>
      <NewChallengeModal
        open={newOpen}
        companyId={companyId}
        onClose={() => setNewOpen(false)}
        onCreated={() => {
          setNewOpen(false);
          state.reload();
        }}
      />
    </section>
  );
}

function ChallengeSection({
  title,
  challenges,
  onStatusError,
  onChanged,
}: {
  title: string;
  challenges: Challenge[];
  onStatusError: (error: Error | null) => void;
  onChanged: () => void;
}) {
  if (challenges.length === 0) return null;

  return (
    <div className="flex flex-col gap-4">
      <h2 className="font-heading text-ink text-lg font-semibold">{title}</h2>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {challenges.map((challenge) => (
          <ChallengeCard
            key={challenge.id}
            challenge={challenge}
            onStatusError={onStatusError}
            onChanged={onChanged}
          />
        ))}
      </div>
    </div>
  );
}

const statusTone: Record<ChallengeStatus, 'outline' | 'mint' | 'warning' | 'danger'> = {
  draft: 'outline',
  active: 'mint',
  paused: 'warning',
  completed: 'outline',
};

function ChallengeCard({
  challenge,
  onStatusError,
  onChanged,
}: {
  challenge: Challenge;
  onStatusError: (error: Error | null) => void;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const leaderboard = useAsync(
    () => (showLeaderboard ? getChallengeLeaderboard(challenge.id) : Promise.resolve([])),
    [challenge.id, showLeaderboard],
  );

  async function move(status: ChallengeStatus) {
    setBusy(true);
    onStatusError(null);
    try {
      await setChallengeStatus(challenge.id, status);
      onChanged();
    } catch (cause) {
      onStatusError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setBusy(false);
    }
  }

  // "Duplicate as draft" (completed cards only, per PLAN-FASE2.md WP-05): a
  // fresh POST /challenges with the same fields, always landing in status
  // draft again — a status move, not a resource clone, matches
  // api_challenges.php's own comment that duplication is client-side, not
  // an endpoint of its own.
  async function duplicate() {
    setBusy(true);
    onStatusError(null);
    try {
      await createChallenge(challenge.company_id, {
        title: `${challenge.title} (${t('challenges.actions.duplicateSuffix')})`,
        description: challenge.description,
        type: challenge.type,
        mode: challenge.mode,
        start_date: challenge.start_date,
        end_date: challenge.end_date,
        goal_value: challenge.goal_value,
        unit: challenge.unit,
        reward: challenge.reward,
        leaderboard_visible: challenge.leaderboard_visible,
      });
      onChanged();
    } catch (cause) {
      onStatusError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardHeader
        title={challenge.title}
        action={
          <Badge tone={statusTone[challenge.status]}>
            {t(`challenges.status.${challenge.status}`)}
          </Badge>
        }
      />
      {challenge.description && <p className="text-ink mb-3 text-sm">{challenge.description}</p>}
      <dl className="text-ink grid grid-cols-2 gap-2 text-sm">
        <div>
          <dt className="text-accent text-[13px]">{t('challenges.card.type')}</dt>
          <dd>{challenge.type}</dd>
        </div>
        <div>
          <dt className="text-accent text-[13px]">{t('challenges.card.mode')}</dt>
          <dd>{t(`challenges.mode.${challenge.mode}`)}</dd>
        </div>
        <div>
          <dt className="text-accent text-[13px]">{t('challenges.card.period')}</dt>
          <dd>
            {challenge.start_date} – {challenge.end_date}
          </dd>
        </div>
        <div>
          <dt className="text-accent text-[13px]">{t('challenges.card.goal')}</dt>
          <dd>
            {challenge.goal_value} {challenge.unit}
          </dd>
        </div>
        {challenge.reward && (
          <div>
            <dt className="text-accent text-[13px]">{t('challenges.card.reward')}</dt>
            <dd>{challenge.reward}</dd>
          </div>
        )}
        <div>
          <dt className="text-accent text-[13px]">{t('challenges.card.participants')}</dt>
          <dd>{challenge.participant_count ?? 0}</dd>
        </div>
      </dl>

      {(challenge.engagement_rate != null || challenge.completion_rate != null) && (
        <div className="mt-3 flex flex-col gap-2">
          {challenge.engagement_rate != null && (
            <div>
              <div className="text-ink mb-1 flex justify-between text-[13px]">
                <span className="text-accent">{t('challenges.card.engagement')}</span>
                <span>{formatNumber(challenge.engagement_rate)}%</span>
              </div>
              <ProgressBar pct={challenge.engagement_rate} />
            </div>
          )}
          {challenge.completion_rate != null && (
            <div>
              <div className="text-ink mb-1 flex justify-between text-[13px]">
                <span className="text-accent">{t('challenges.card.completion')}</span>
                <span>{formatNumber(challenge.completion_rate)}%</span>
              </div>
              <ProgressBar pct={challenge.completion_rate} />
            </div>
          )}
        </div>
      )}

      <div className="mt-3">
        {challenge.leaderboard_visible ? (
          <Badge tone="mint">{t('challenges.card.leaderboardVisible')}</Badge>
        ) : (
          <Badge tone="outline">{t('challenges.card.leaderboardHidden')}</Badge>
        )}
      </div>

      {challenge.leaderboard_visible && (
        <div className="mt-3">
          <Button variant="ghost" size="sm" onClick={() => setShowLeaderboard((open) => !open)}>
            {showLeaderboard
              ? t('challenges.actions.hideLeaderboard')
              : t('challenges.actions.viewLeaderboard')}
          </Button>
          {showLeaderboard && (
            <AsyncSection state={leaderboard} label={t('challenges.leaderboard.title')}>
              {(rows) =>
                rows.length === 0 ? (
                  <p className="text-accent mt-2 text-sm">{t('challenges.leaderboard.empty')}</p>
                ) : (
                  <ol className="text-ink mt-2 flex flex-col gap-1 text-sm">
                    {rows.map((row) => (
                      <li key={row.user_id} className="flex justify-between">
                        <span>
                          {row.rank}. {row.name ?? row.user_id}
                        </span>
                        <span>{row.progress_value}</span>
                      </li>
                    ))}
                  </ol>
                )
              }
            </AsyncSection>
          )}
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-2">
        {challenge.status === 'draft' && (
          <Button size="sm" disabled={busy} onClick={() => void move('active')}>
            {t('challenges.actions.publish')}
          </Button>
        )}
        {challenge.status === 'active' && (
          <>
            <Button
              size="sm"
              variant="secondary"
              disabled={busy}
              onClick={() => void move('paused')}
            >
              {t('challenges.actions.pause')}
            </Button>
            <Button
              size="sm"
              variant="danger"
              disabled={busy}
              onClick={() => void move('completed')}
            >
              {t('challenges.actions.finish')}
            </Button>
          </>
        )}
        {challenge.status === 'paused' && (
          <>
            <Button size="sm" disabled={busy} onClick={() => void move('active')}>
              {t('challenges.actions.resume')}
            </Button>
            <Button
              size="sm"
              variant="danger"
              disabled={busy}
              onClick={() => void move('completed')}
            >
              {t('challenges.actions.finish')}
            </Button>
          </>
        )}
        {challenge.status === 'completed' && (
          <Button size="sm" variant="ghost" disabled={busy} onClick={() => void duplicate()}>
            {t('challenges.actions.duplicate')}
          </Button>
        )}
      </div>
    </Card>
  );
}
